import React from 'react';
import { fetchMe, getCachedMe, subscribeMeCacheInvalidation, type MeResponse } from '../lib/meClient';
import {
  type InstanceModuleKey,
  type InstanceModuleMap,
  canOpenAppPath,
  defaultModuleMap,
  isCapabilityAvailable,
  isModuleEnabled,
  normalizeModuleMap,
} from '../lib/instanceModules';

type Snapshot = {
  modules: InstanceModuleMap | null;
  capabilities: Record<string, boolean> | null;
  /** Aktive Instanz der Sitzung, zu der modules gehören. */
  instanceId: string | null;
  /** Instanzen des Nutzers (für die Erkennung eines ausstehenden Instanzwechsels). */
  instanceIds: string[];
};

const EMPTY: Snapshot = { modules: null, capabilities: null, instanceId: null, instanceIds: [] };
const STALE_MS = 5_000;

function snapshotFromMe(me: MeResponse | null): Snapshot {
  if (!me) return EMPTY;
  return {
    modules: normalizeModuleMap(me.modules),
    capabilities: (me.capabilities as Record<string, boolean> | undefined) ?? {},
    instanceId: me.activeInstance?.id ? String(me.activeInstance.id) : null,
    instanceIds: Array.isArray(me.instances) ? me.instances.map((i) => String(i.id)) : [],
  };
}

const keyOf = (s: Snapshot) => JSON.stringify([s.modules, s.capabilities, s.instanceId, s.instanceIds]);

// Ein Store für alle Aufrufer (ModuleLink sitzt in langen Listen): ein /api/me-Aufruf je
// Zeitfenster, und Listener werden nur bei einer echten Änderung benachrichtigt.
let snapshot: Snapshot = snapshotFromMe(getCachedMe());
let snapshotKey = keyOf(snapshot);
let lastLoadAt = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();
let started = false;

function emit(next: Snapshot): void {
  const nextKey = keyOf(next);
  if (nextKey === snapshotKey) return;
  snapshot = next;
  snapshotKey = nextKey;
  for (const listener of listeners) listener();
}

function load(force = false): void {
  if (inflight) return;
  if (!force && snapshot.modules && Date.now() - lastLoadAt < STALE_MS) return;
  inflight = fetchMe()
    .then((me) => {
      lastLoadAt = Date.now();
      if (me) emit(snapshotFromMe(me));
      else if (!snapshot.modules) emit({ ...EMPTY, modules: defaultModuleMap(), capabilities: {} });
    })
    .catch(() => {
      if (!snapshot.modules) emit({ ...EMPTY, modules: defaultModuleMap(), capabilities: {} });
    })
    .finally(() => {
      inflight = null;
    });
}

function ensureStarted(): void {
  if (started) return;
  started = true;
  // Instanzwechsel, Logout: Cache wurde geleert, also neu laden.
  subscribeMeCacheInvalidation(() => load(true));
  if (typeof document !== 'undefined') {
    // Admin-Center-Schalter greifen, sobald der Tab wieder im Vordergrund ist.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') load();
    });
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  ensureStarted();
  load();
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => snapshot;

function urlInstanceId(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/i\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Module der aktiven Instanz (Admin-Center-Schalter) plus Rechte, aus dem /api/me-Cache.
 * `canOpen(path)` sagt für Quer-Links, ob die Zielseite erreichbar ist: Modul an und Recht da.
 * Zeigt die URL auf eine andere eigene Instanz als die Sitzung (Wechsel läuft noch), gilt der Stand
 * als noch nicht geladen, damit keine Entscheidung mit den Modulen der falschen Instanz fällt.
 */
export function useModuleAccess() {
  const current = React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const urlInstance = urlInstanceId();
  const switchPending = Boolean(
    current.instanceId &&
      urlInstance &&
      urlInstance !== current.instanceId &&
      current.instanceIds.includes(urlInstance),
  );
  const modules = switchPending ? null : current.modules;
  const capabilities = switchPending ? null : current.capabilities;

  const isEnabled = React.useCallback((key: InstanceModuleKey) => isModuleEnabled(modules, key), [modules]);
  const capabilityAvailable = React.useCallback(
    (capability: string) => isCapabilityAvailable(capability, modules),
    [modules],
  );
  const canOpen = React.useCallback(
    (path: string) => modules !== null && canOpenAppPath(path, modules, capabilities),
    [modules, capabilities],
  );

  return {
    loaded: modules !== null,
    modules,
    isEnabled,
    isCapabilityAvailable: capabilityAvailable,
    canOpen,
  };
}
